import { formatCurrency } from "@angular/common";
import { Component, computed, inject, input, LOCALE_ID } from "@angular/core";
import { ChartJsConfigBuilder, ChartJsContainerComponent, Series } from "@tylertech/chart-js";
import { ChartService } from "@tylertech/charting-module";
import { getLoggerFor } from "@tylertech/logging";
import { IInBalanceCheck, IInBalanceItem } from "@tylertech/tyler-api-types";
import { ChartConfiguration, TooltipItem } from "chart.js";

@Component({
    selector: "in-balance-chart",
    imports: [
        ChartJsContainerComponent,
    ],
    templateUrl: "./inBalanceChart.tmpl.html",
})
export class InBalanceChartComponent
{
    private readonly $chartService = inject(ChartService);

    private readonly log = getLoggerFor("InBalanceChartComponent");

    private readonly locale: string = inject(LOCALE_ID);

    readonly check = input.required<IInBalanceCheck>();

    readonly isZeroCase = computed(() =>
    {
        const inBalanceCheck = this.check();
        //Checking to see if both values are equal to zero and should show full bar graph
        return inBalanceCheck.Item2.Value === inBalanceCheck.Item1.Value
            && inBalanceCheck.Item2.Value === 0;
    });

    readonly chartConfig = computed(() =>
    {
        const inBalanceCheck = this.check();
        const isZeroCase = this.isZeroCase();
        const dataColors = this.$chartService.getColors(5);
        const blueColor = dataColors[1];
        const orangeColor = dataColors[2];
        const grayColor = dataColors[4];

        const config = new ChartJsConfigBuilder<IInBalanceItem>(inBalanceCheck.Name, "bar")
            .setResponsive(true)
            // .setMaintainAspectRatio(true)
            .setTitle({
                display: false,
                text: inBalanceCheck.Name,
            })
            .setLegend({ display: false })
            .setTooltips({
                mode: "dataset",
                callbacks: {
                    label: (tooltipItem: TooltipItem<"bar">): string | string[] =>
                    {
                        if (isZeroCase)
                        {
                            return `${tooltipItem.label}: 0`;
                        }
                        const amount = inBalanceCheck.IsCurrency
                            ? formatCurrency(tooltipItem.raw as number, this.locale, "$")
                            : tooltipItem.raw;
                        return `${tooltipItem.label}: ${amount}`;
                    },
                    // Title is not used in hover text. Items explain what the values mean.
                    title: (_tooltipItems: Array<TooltipItem<"bar">>) => null,
                },
                borderWidth: 1,
            })
            .setIndexAxis("y")
            .addScales({
                x: {
                    type: "linear",
                    display: false,
                    beginAtZero: true,
                    max: isZeroCase ? 1 : Math.max(inBalanceCheck.Item1.Value, inBalanceCheck.Item2.Value),
                    ticks: {
                        display: false,
                    },
                },
                y: {
                    type: "category",
                    display: false,
                    ticks: {
                        display: false,
                    },
                },
            })
            .addSeries({
                type: "bar",
                name: inBalanceCheck.Name,
                labels: [
                    inBalanceCheck.Item1.Name,
                    inBalanceCheck.Item2.Name,
                ],
                barThickness: 0.5,
                getValue: (v, _i) =>
                {
                    if (isZeroCase)
                    {
                        // In the case we have double 0 comparison we want the graph to show in balance and full
                        return 1;
                    }

                    return v.Value;
                },
            } satisfies Series<IInBalanceItem, "bar">)
            .setCategories([
                inBalanceCheck.Item1.Name,
                inBalanceCheck.Item2.Name,
            ])
            .build([
                inBalanceCheck.Item1,
                inBalanceCheck.Item2,
            ]) as ChartConfiguration<"bar">;
        this.log.debug(`config`, config);

        config.data.datasets[0].backgroundColor = [
            inBalanceCheck.Variance === 0
                ? blueColor
                : orangeColor,
            grayColor];

        config.data.datasets[0].borderColor = "transparent";

        return config;
    });
}
